import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { AdminLayout } from '../components/AdminLayout';
import { KPICard } from '../components/KPICard';
import { useEngagement } from '../hooks/useAdminStats';
import { useAdminFilter } from '../components/AdminSidebar';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Activity,
  Zap,
  UserMinus,
  CalendarCheck,
  CalendarDays,
  Calendar,
  RefreshCw,
} from 'lucide-react';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString('fr-FR');
}

export default function EngagementPage() {
  const { excludeAdmins } = useAdminFilter();
  const { data, isLoading, error, refetch } = useEngagement(excludeAdmins);
  const { isDarkMode } = useTheme();

  const activeUsersChartOption = useMemo(() => {
    if (!data?.active_users_over_time) return {};
    const days = data.active_users_over_time.map((d) => d.day);
    const counts = data.active_users_over_time.map((d) => d.active_users);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      grid: { top: 20, right: 20, bottom: 30, left: 50 },
      xAxis: {
        type: 'category',
        data: days,
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: {
          color: isDarkMode ? '#9ca3af' : '#6b7280',
          fontSize: 10,
          formatter: (v: string) => v.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      series: [
        {
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: { color: '#16c47f', width: 3 },
          itemStyle: { color: '#16c47f' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#16c47f25' },
                { offset: 1, color: '#16c47f00' },
              ],
            },
          },
        },
      ],
    };
  }, [data, isDarkMode]);

  const retentionChartOption = useMemo(() => {
    if (!data?.retention_cohorts) return {};
    const cohorts = data.retention_cohorts;

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
        formatter: (params: { data?: unknown; name?: string; seriesName?: string }) => {
          if (params.data === undefined || params.data === null) return '';
          const val =
            typeof params.data === 'object' && params.data !== null && 'value' in params.data
              ? (params.data as { value: number }).value
              : params.data;
          return `${params.name}<br/>Semaine ${params.seriesName}: <b>${val}%</b>`;
        },
      },
      grid: { top: 30, right: 20, bottom: 40, left: 100 },
      xAxis: {
        type: 'category',
        data: ['Sem. 1', 'Sem. 2', 'Sem. 3', 'Sem. 4'],
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      yAxis: {
        type: 'category',
        data: cohorts.map((c) => c.cohort_week).reverse(),
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: { color: isDarkMode ? '#d1d5db' : '#374151', fontSize: 10 },
      },
      visualMap: {
        min: 0,
        max: 100,
        calculable: false,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: {
          color: ['#f0fdf4', '#16c47f20', '#16c47f60', '#16c47f'],
        },
        textStyle: { color: isDarkMode ? '#9ca3af' : '#6b7280' },
      },
      series: [
        {
          type: 'heatmap',
          data: cohorts.flatMap((c, yi) => {
            const size = c.cohort_size || 1;
            const ryi = cohorts.length - 1 - yi;
            return [
              [0, ryi, Math.round((c.week_1 / size) * 100)],
              [1, ryi, Math.round((c.week_2 / size) * 100)],
              [2, ryi, Math.round((c.week_3 / size) * 100)],
              [3, ryi, Math.round((c.week_4 / size) * 100)],
            ];
          }),
          label: {
            show: true,
            formatter: (params: { data: number[] }) => `${params.data[2]}%`,
            color: isDarkMode ? '#f9fafb' : '#111827',
            fontSize: 11,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: isDarkMode ? '#1f2937' : '#fff',
            borderRadius: 4,
          },
        },
      ],
    };
  }, [data, isDarkMode]);

  if (isLoading) {
    return (
      <AdminLayout title="Engagement & Retention" subtitle="Activité et rétention des utilisateurs">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
            >
              <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
              <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Engagement & Retention" subtitle="Activité et rétention des utilisateurs">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl hover:bg-red-200 transition"
          >
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Engagement & Retention"
      subtitle="Activité et rétention des utilisateurs"
      action={
        <button
          onClick={refetch}
          className="p-2 rounded-xl text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/10 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      }
    >
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard
          title="DAU"
          value={formatNumber(data?.dau ?? 0)}
          icon={Activity}
          gradient="from-emerald-500 to-emerald-600"
          subtitle="Utilisateurs Actifs Aujourd'hui"
        />
        <KPICard
          title="WAU"
          value={formatNumber(data?.wau ?? 0)}
          icon={CalendarCheck}
          gradient="from-blue-500 to-blue-600"
          subtitle="Utilisateurs Actifs Cette Semaine"
        />
        <KPICard
          title="MAU"
          value={formatNumber(data?.mau ?? 0)}
          icon={CalendarDays}
          gradient="from-violet-500 to-violet-600"
          subtitle="Utilisateurs Actifs Ce Mois"
        />
        <KPICard
          title="Stickiness"
          value={`${data?.stickiness ?? 0}%`}
          icon={Zap}
          gradient="from-amber-500 to-amber-600"
          subtitle="DAU / MAU"
        />
        <KPICard
          title="Churn Rate"
          value={`${data?.churn_rate ?? 0}%`}
          icon={UserMinus}
          gradient="from-red-500 to-red-600"
          subtitle="Taux d'attrition mensuel"
        />
        <KPICard
          title="Rétention D1"
          value={`${data?.retention_d1 ?? 0}%`}
          icon={Calendar}
          gradient="from-[#16c47f] to-emerald-600"
          subtitle="Retour après 1 jour"
        />
        <KPICard
          title="Rétention D7"
          value={`${data?.retention_d7 ?? 0}%`}
          icon={Calendar}
          gradient="from-cyan-500 to-cyan-600"
          subtitle="Retour après 7 jours"
        />
        <KPICard
          title="Rétention D30"
          value={`${data?.retention_d30 ?? 0}%`}
          icon={Calendar}
          gradient="from-pink-500 to-pink-600"
          subtitle="Retour après 30 jours"
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Active users over time */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Utilisateurs Actifs
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Activité quotidienne sur 90 jours
          </p>
          {data?.active_users_over_time?.length ? (
            <ReactECharts option={activeUsersChartOption} style={{ height: 300 }} />
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>

        {/* Retention cohorts heatmap */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Cohortes de Rétention
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Rétention hebdomadaire par cohorte d'inscription
          </p>
          {data?.retention_cohorts?.length ? (
            <ReactECharts option={retentionChartOption} style={{ height: 300 }} />
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>
      </div>

      {/* Retention funnel visual */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          Funnel de Rétention
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
          Pourcentage d'utilisateurs qui reviennent
        </p>
        <div className="flex items-end justify-center gap-8 h-48">
          {[
            { label: 'D1', value: data?.retention_d1 ?? 0, color: 'from-[#16c47f] to-emerald-400' },
            { label: 'D7', value: data?.retention_d7 ?? 0, color: 'from-cyan-500 to-cyan-400' },
            { label: 'D30', value: data?.retention_d30 ?? 0, color: 'from-pink-500 to-pink-400' },
          ].map((item) => (
            <div key={item.label} className="flex flex-col items-center gap-2">
              <span className="text-lg font-bold text-gray-900 dark:text-white">{item.value}%</span>
              <div
                className={`w-20 bg-gradient-to-t ${item.color} rounded-t-xl transition-all duration-700`}
                style={{ height: `${Math.max(item.value * 1.5, 8)}px` }}
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
